import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, LoadingBlock, EmptyState } from "@/components/Common";
import api, { formatApiError } from "@/lib/api";
import { getBlogCategories } from "@/data/site";
import { useI18n, useL } from "@/context/I18nContext";

const STATUS_OPTIONS = ["draft", "review", "published", "archived"];

export default function AdminContent() {
  const L = useL();
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Contenuti (CMS)", "Content (CMS)", "Inhalte (CMS)")}</h2>
      <Tabs defaultValue="faq">
        <TabsList className="bg-navy-paper border border-white/10">
          <TabsTrigger value="faq" data-testid="cms-tab-faq">FAQ</TabsTrigger>
          <TabsTrigger value="blog" data-testid="cms-tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="cms-tab-reviews">{L("Recensioni", "Reviews", "Bewertungen")}</TabsTrigger>
        </TabsList>
        <TabsContent value="faq" className="mt-4"><FaqManager /></TabsContent>
        <TabsContent value="blog" className="mt-4"><BlogManager /></TabsContent>
        <TabsContent value="reviews" className="mt-4"><ReviewManager /></TabsContent>
      </Tabs>
    </div>
  );
}

function FaqManager() {
  const L = useL();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ question: "", answer: "", category: "general", status: "published" });
  const load = () => api.get("/content/faq").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/faq", { ...f, order: (items?.length || 0) }); toast.success(L("FAQ aggiunta", "FAQ added", "FAQ hinzugefügt")); setF({ question: "", answer: "", category: "general", status: "published" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/faq/${id}`); toast.success(L("Eliminata", "Deleted", "Gelöscht")); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg text-white">{L("Nuova FAQ", "New FAQ", "Neue FAQ")}</h3>
        <div><Label className="text-slate-300">{L("Domanda", "Question", "Frage")}</Label><Input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="faq-question" /></div>
        <div><Label className="text-slate-300">{L("Risposta", "Answer", "Antwort")}</Label><Textarea rows={3} value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="faq-answer" /></div>
        <Button onClick={add} disabled={!f.question || !f.answer} className="bg-gold text-navy hover:bg-gold-hover" data-testid="faq-add"><Plus className="h-4 w-4 mr-1.5" />{L("Aggiungi", "Add", "Hinzufügen")}</Button>
      </Card>
      <div className="space-y-2">
        {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState title={L("Nessuna FAQ", "No FAQ", "Keine FAQ")} /> :
          items.map((q) => (
            <Card key={q.id} className="p-4 flex items-start justify-between gap-3" data-testid={`faq-item-${q.id}`}>
              <div><p className="text-sm text-white">{q.question}</p><p className="text-xs text-slate-500 mt-1 line-clamp-2">{q.answer}</p></div>
              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(q.id)} data-testid={`faq-del-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
            </Card>
          ))}
      </div>
    </div>
  );
}

function BlogManager() {
  const L = useL();
  const { lang } = useI18n();
  const categories = getBlogCategories(lang);
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ title: "", slug: "", excerpt: "", body: "", category: categories[0], status: "draft" });
  const load = () => api.get("/admin/blog").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      const slug = f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      await api.post("/admin/blog", { ...f, slug });
      toast.success(L("Articolo creato", "Article created", "Artikel erstellt")); setF({ title: "", slug: "", excerpt: "", body: "", category: categories[0], status: "draft" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updateStatus = async (id, status) => { await api.patch(`/admin/blog/${id}`, { status }); toast.success(L("Stato aggiornato", "Status updated", "Status aktualisiert")); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg text-white">{L("Nuovo articolo", "New article", "Neuer Artikel")}</h3>
        <div><Label className="text-slate-300">{L("Titolo", "Title", "Titel")}</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="blog-title" /></div>
        <div><Label className="text-slate-300">{L("Estratto", "Excerpt", "Auszug")}</Label><Textarea rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="blog-excerpt" /></div>
        <div><Label className="text-slate-300">{L("Contenuto", "Content", "Inhalt")}</Label><Textarea rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="blog-body" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300">{L("Categoria", "Category", "Kategorie")}</Label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><Label className="text-slate-300">{L("Stato", "Status", "Status")}</Label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="blog-status">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={add} disabled={!f.title} className="bg-gold text-navy hover:bg-gold-hover" data-testid="blog-add"><Plus className="h-4 w-4 mr-1.5" />{L("Crea", "Create", "Erstellen")}</Button>
      </Card>
      <div className="space-y-2">
        {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState title={L("Nessun articolo", "No articles", "Keine Artikel")} /> :
          items.map((p) => (
            <Card key={p.id} className="p-4 flex items-center justify-between gap-3" data-testid={`blog-item-${p.id}`}>
              <div><p className="text-sm text-white">{p.title}</p><p className="text-xs text-slate-500">{p.category}</p></div>
              <select value={p.status} onChange={(e) => updateStatus(p.id, e.target.value)} className="h-9 rounded-md bg-navy border border-white/10 px-2 text-xs text-white" data-testid={`blog-status-${p.id}`}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Card>
          ))}
      </div>
    </div>
  );
}

function ReviewManager() {
  const L = useL();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ author_label: "", rating: 5, body: "", status: "draft" });
  const load = () => api.get("/admin/reviews").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/reviews", f); toast.success(L("Recensione creata", "Review created", "Bewertung erstellt")); setF({ author_label: "", rating: 5, body: "", status: "draft" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updateStatus = async (id, status) => { await api.patch(`/admin/reviews/${id}`, { status }); toast.success(L("Stato aggiornato", "Status updated", "Status aktualisiert")); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5 space-y-3 h-fit">
        <h3 className="font-heading text-lg text-white">{L("Nuova recensione", "New review", "Neue Bewertung")}</h3>
        <p className="text-xs text-slate-500">{L("Le recensioni restano non pubblicate finché non vengono approvate manualmente.", "Reviews stay unpublished until manually approved.", "Bewertungen bleiben unveröffentlicht, bis sie manuell freigegeben werden.")}</p>
        <div><Label className="text-slate-300">{L("Autore (etichetta)", "Author (label)", "Autor (Bezeichnung)")}</Label><Input value={f.author_label} onChange={(e) => setF({ ...f, author_label: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="review-author" /></div>
        <div><Label className="text-slate-300">{L("Valutazione (1-5)", "Rating (1-5)", "Bewertung (1-5)")}</Label><Input type="number" min={1} max={5} value={f.rating} onChange={(e) => setF({ ...f, rating: parseInt(e.target.value) || 5 })} className="mt-1.5 bg-navy border-white/10" /></div>
        <div><Label className="text-slate-300">{L("Testo", "Text", "Text")}</Label><Textarea rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="review-body" /></div>
        <Button onClick={add} disabled={!f.author_label || !f.body} className="bg-gold text-navy hover:bg-gold-hover" data-testid="review-add"><Plus className="h-4 w-4 mr-1.5" />{L("Aggiungi", "Add", "Hinzufügen")}</Button>
      </Card>
      <div className="space-y-2">
        {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState title={L("Nessuna recensione", "No reviews", "Keine Bewertungen")} /> :
          items.map((r) => (
            <Card key={r.id} className="p-4 flex items-center justify-between gap-3" data-testid={`review-item-${r.id}`}>
              <div><p className="text-sm text-white">{r.author_label} · {r.rating}★</p><p className="text-xs text-slate-500 line-clamp-2">{r.body}</p></div>
              <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} className="h-9 rounded-md bg-navy border border-white/10 px-2 text-xs text-white" data-testid={`review-status-${r.id}`}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Card>
          ))}
      </div>
    </div>
  );
}
